// Minimal single-stroke line icons for the org-admin sidebar, matching the
// reference product's plain outline style (no emoji, no fill). All share
// the same viewBox/stroke settings so they sit consistently at any size;
// color always comes from the surrounding item (currentColor), so active/
// hover states apply automatically without per-icon overrides.
const ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

// The AI tab: a search glass with a spark. Deliberately search-shaped rather
// than robot-shaped — what the section actually does most of the time is find
// things, and the command bar is the second half.
export const AiIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="10.5" cy="10.5" r="6" />
    <path d="M15 15l5 5" />
    <path d="M17.5 3.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
  </svg>
);

export const DashboardIcon = () => (
  <svg {...ICON_PROPS}>
    <rect x="3.5" y="3.5" width="7.5" height="7.5" rx="1.6" />
    <rect x="13" y="3.5" width="7.5" height="7.5" rx="1.6" />
    <rect x="3.5" y="13" width="7.5" height="7.5" rx="1.6" />
    <rect x="13" y="13" width="7.5" height="7.5" rx="1.6" />
  </svg>
);

export const ClientsIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M4 20V9.5a1.5 1.5 0 0 1 1.5-1.5h13A1.5 1.5 0 0 1 20 9.5V20" />
    <path d="M9 8V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    <path d="M4 13h16" />
    <path d="M10.5 13v2.5a1.5 1.5 0 0 0 3 0V13" />
  </svg>
);

export const CredentialsIcon = () => (
  <svg {...ICON_PROPS}>
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    <circle cx="12" cy="15.4" r="1.1" fill="currentColor" stroke="none" />
  </svg>
);

export const ServicesIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M12 3.5 20.5 8 12 12.5 3.5 8Z" />
    <path d="M3.5 12 12 16.5 20.5 12" />
    <path d="M3.5 16 12 20.5 20.5 16" />
  </svg>
);

export const UsersIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 20v-1.5A4.5 4.5 0 0 1 8 14h2a4.5 4.5 0 0 1 4.5 4.5V20" />
    <path d="M16 8.2a3 3 0 1 1 0 6" />
    <path d="M15 14.2h.8a4.2 4.2 0 0 1 4.2 4.2V20" />
  </svg>
);

export const PermissionsIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M12 3.5l7 2.8v5.2c0 4.4-2.9 7.6-7 9-4.1-1.4-7-4.6-7-9V6.3l7-2.8z" />
    <path d="M9.2 12.2l2 2 3.6-3.9" />
  </svg>
);

export const ClientNotesIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M5 4.5h11l3 3v12H5z" />
    <path d="M8.5 9.5h7M8.5 13h7M8.5 16.5h4" />
    <path d="M16 4.5v3h3" />
  </svg>
);

export const NoticeIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M6 3.5h8.5L19 8v12.5H6z" />
    <path d="M14 3.5V8h4.5" />
    <path d="M9 12.5h6M9 16h4" />
    <circle cx="17.5" cy="6.5" r="3" fill="var(--ds-danger-solid, #9c5b3e)" stroke="none" />
  </svg>
);

export const SettingsIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="12" cy="12" r="4" />
    <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
    {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
      <line key={angle} x1="12" y1="7.3" x2="12" y2="4.8" transform={`rotate(${angle} 12 12)`} />
    ))}
  </svg>
);

export const TasksIcon = () => (
  <svg {...ICON_PROPS}>
    <rect x="4" y="4" width="16" height="16" rx="3" />
    <path d="M8.5 12.5 11 15l5-5.5" />
  </svg>
);

export const TrashIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M4.5 7h15" />
    <path d="M9 7V5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 5v2" />
    <path d="M6.5 7 7.3 19a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7" />
    <path d="M10.3 11v6M13.7 11v6" />
  </svg>
);

export const InboxIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M4 12.5h4.2l1.4 2.2h4.8l1.4-2.2H20" />
    <path d="M6 5.5h12L20 12.5v5A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5v-5Z" />
  </svg>
);

export const ChatIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M20 11.8a7.4 7.4 0 0 1-7.4 7.4c-1.2 0-2.4-.28-3.4-.79L4.5 19.5l1.3-3.9A7.4 7.4 0 1 1 20 11.8Z" />
    <path d="M9.2 10.4h6M9.2 13.4h4" />
  </svg>
);

export const CheckCircleIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="12" cy="12" r="8.2" />
    <path d="M8.3 12.3 10.8 14.8 15.7 9.6" />
  </svg>
);

export const XCircleIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="12" cy="12" r="8.2" />
    <path d="M9.3 9.3 14.7 14.7M14.7 9.3 9.3 14.7" />
  </svg>
);

export const CalendarIcon = () => (
  <svg {...ICON_PROPS}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
    <path d="M3.5 9.5h17" />
    <path d="M8 3v3.5M16 3v3.5" />
  </svg>
);

export const LogoutIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M13 4H6.5A1.5 1.5 0 0 0 5 5.5v13A1.5 1.5 0 0 0 6.5 20H13" />
    <path d="M16.5 15.5 20 12l-3.5-3.5" />
    <path d="M20 12H9.5" />
  </svg>
);

export const ToDoIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M9 6h11M9 12h11M9 18h11" />
    <path d="M4 5.5l1.2 1.2L7.5 4.3" />
    <circle cx="5" cy="12" r="0.6" fill="currentColor" stroke="none" />
    <circle cx="5" cy="18" r="0.6" fill="currentColor" stroke="none" />
  </svg>
);

export const RetainersIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M12 3.5l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 9.2l5.4-.8Z" />
  </svg>
);

export const FinanceIcon = () => (
  <svg {...ICON_PROPS}>
    <rect x="3" y="5.5" width="18" height="13" rx="2" />
    <path d="M3 9.5h18" />
    <circle cx="12" cy="14" r="1.8" />
  </svg>
);

export const AttendanceIcon = () => (
  <svg {...ICON_PROPS}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
    <path d="M3.5 9.5h17M8 3v3.5M16 3v3.5" />
    <path d="M9 14.5l2 2 3.5-3.8" />
  </svg>
);

export const RenewalsIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M19.5 8.5A8 8 0 1 0 20 12" />
    <path d="M20 4v4.5h-4.5" />
  </svg>
);

export const DocumentsIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M13 3.5H7A1.5 1.5 0 0 0 5.5 5v14A1.5 1.5 0 0 0 7 20.5h10A1.5 1.5 0 0 0 18.5 19V9Z" />
    <path d="M13 3.5V9h5.5M8.5 13.5h7M8.5 16.5h7" />
  </svg>
);

export const ReportsIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M4 20h16" />
    <path d="M7 20V11M12 20V5M17 20v-6" />
  </svg>
);

// A written record — deliberately not a second chart. Reports summarise the
// work; this is the account of who did it.
export const AuditLogsIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M9 12h6M9 16h4" />
  </svg>
);

export const SendNotificationsIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M20.5 3.5 10.5 13.5" />
    <path d="M20.5 3.5 14 20.5l-3.5-7-7-3.5Z" />
  </svg>
);

export const WebsiteIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17M12 3.5c2.5 2.4 2.5 14.6 0 17M12 3.5c-2.5 2.4-2.5 14.6 0 17" />
  </svg>
);

export const PhysicalFilesIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M4 7.5A1.5 1.5 0 0 1 5.5 6h3l1.6 2H18.5A1.5 1.5 0 0 1 20 9.5v8A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5Z" />
    <path d="M8 12.5h8" />
  </svg>
);

export const DscIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M6 3.5h8.5L19 8v7.5" />
    <path d="M14 3.5V8h4.5" />
    <path d="M4 20.5c1.2-.4 1.9-1.3 2.6-2.8l3-6.3c.4-.9 1.6-.9 2 0l1.9 4.2" />
    <path d="M15.5 18.5c1.5 0 1.5-2 3-2" />
  </svg>
);

export const PlusIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M12 5.5v13M5.5 12h13" />
  </svg>
);

export const ChevronRightIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M9.5 6l6 6-6 6" />
  </svg>
);

export const ExternalLinkIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M14 4h6v6M20 4l-8.5 8.5" />
    <path d="M18 13.5V19a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 19V8a1.5 1.5 0 0 1 1.5-1.5H11" />
  </svg>
);

// A clock — used for the collapsed "Coming soon" nav group of not-yet-built features.
export const ComingSoonIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
